import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";

const PrivacyPolicyPage = () => {
  const currentYear = new Date().getFullYear();
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <a href="/" className="text-2xl font-heading font-bold text-[#e6007e]">Paify</a>
        </div>
      </header>
      
      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-4xl font-heading font-bold text-gray-900 mb-8">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mb-8">Last updated: January {currentYear}</p>
        
        <div className="prose prose-gray max-w-none space-y-8">
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-heading font-semibold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-600 font-body leading-relaxed">
              Paify ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our payment management platform and related services.
            </p>
            <p className="text-gray-600 font-body leading-relaxed mt-4">
              By using Paify, you agree to the collection and use of information in accordance with this policy. If you do not agree with the terms of this privacy policy, please do not access the platform.
            </p>
          </section>
          
          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-heading font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
            
            <h3 className="text-lg font-heading font-medium text-gray-800 mb-2">2.1 Personal Information</h3>
            <p className="text-gray-600 font-body leading-relaxed mb-4">
              We may collect personally identifiable information that you voluntarily provide, including but not limited to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 font-body space-y-2">
              <li>Name and contact information (email address, phone number)</li>
              <li>Business information (company name, business address, VAT number)</li>
              <li>Account credentials (email and encrypted password)</li>
              <li>Payment information (processed securely through Stripe)</li>
              <li>Electronic signatures for contract agreements</li>
            </ul>
            
            <h3 className="text-lg font-heading font-medium text-gray-800 mb-2 mt-6">2.2 Automatically Collected Information</h3>
            <p className="text-gray-600 font-body leading-relaxed">
              When you access our platform, we may automatically collect certain information, including:
            </p>
            <ul className="list-disc pl-6 text-gray-600 font-body space-y-2 mt-2">
              <li>Device and browser information</li>
              <li>IP address and location data</li>
              <li>Usage data and interaction patterns</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>
          </section>
          
          {/* How We Use Your Information */}
          <section>
            <h2 className="text-2xl font-heading font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-600 font-body leading-relaxed mb-4">
              We use the information we collect to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 font-body space-y-2">
              <li>Provide, operate, and maintain our platform</li>
              <li>Process payments and transactions through our payment processor (Stripe)</li>
              <li>Create and manage your account</li>
              <li>Generate contracts and facilitate electronic signatures</li>
              <li>Send transactional emails (payment confirmations, contract notifications)</li>
              <li>Respond to your inquiries and provide customer support</li>
              <li>Improve our services and develop new features</li>
              <li>Comply with legal obligations and protect our rights</li>
            </ul>
          </section>
          
          {/* Payment Processing */}
          <section>
            <h2 className="text-2xl font-heading font-semibold text-gray-900 mb-4">4. Payment Processing</h2>
            <p className="text-gray-600 font-body leading-relaxed">
              We use Stripe as our payment processor. When you make a payment through Paify, your payment information is transmitted directly to Stripe and is subject to Stripe's Privacy Policy. We do not store complete credit card numbers or bank account details on our servers.
            </p>
            <p className="text-gray-600 font-body leading-relaxed mt-4">
              Stripe is certified as a PCI Level 1 Service Provider, the most stringent level of certification available in the payments industry.
            </p>
          </section>
          
          {/* Data Sharing */}
          <section>
            <h2 className="text-2xl font-heading font-semibold text-gray-900 mb-4">5. Data Sharing and Disclosure</h2>
            <p className="text-gray-600 font-body leading-relaxed mb-4">
              We may share your information in the following circumstances:
            </p>
            <ul className="list-disc pl-6 text-gray-600 font-body space-y-2">
              <li><strong>Service Providers:</strong> With third-party vendors who assist in operating our platform (e.g., Stripe for payments, email service providers)</li>
              <li><strong>Business Partners:</strong> With businesses you engage with through our platform for the purpose of fulfilling contracts and payments</li>
              <li><strong>Legal Requirements:</strong> When required by law, regulation, or legal process</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </ul>
            <p className="text-gray-600 font-body leading-relaxed mt-4">
              We do not sell your personal information to third parties.
            </p>
          </section>
          
          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-heading font-semibold text-gray-900 mb-4">6. Data Security</h2>
            <p className="text-gray-600 font-body leading-relaxed">
              We implement appropriate technical and organisational measures to protect your personal information against unauthorised access, alteration, disclosure, or destruction. These measures include encryption, secure servers, and access controls.
            </p>
            <p className="text-gray-600 font-body leading-relaxed mt-4">
              However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect your personal information, we cannot guarantee its absolute security.
            </p>
          </section>
          
          {/* Your Rights (GDPR) */}
          <section>
            <h2 className="text-2xl font-heading font-semibold text-gray-900 mb-4">7. Your Rights (UK GDPR)</h2>
            <p className="text-gray-600 font-body leading-relaxed mb-4">
              Under the UK General Data Protection Regulation (UK GDPR), you have the following rights:
            </p>
            <ul className="list-disc pl-6 text-gray-600 font-body space-y-2">
              <li><strong>Right of Access:</strong> Request a copy of the personal data we hold about you</li>
              <li><strong>Right to Rectification:</strong> Request correction of inaccurate or incomplete data</li>
              <li><strong>Right to Erasure:</strong> Request deletion of your personal data in certain circumstances</li>
              <li><strong>Right to Restrict Processing:</strong> Request limitation of how we use your data</li>
              <li><strong>Right to Data Portability:</strong> Request transfer of your data to another service</li>
              <li><strong>Right to Object:</strong> Object to processing of your personal data</li>
              <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time where processing is based on consent</li>
            </ul>
            <p className="text-gray-600 font-body leading-relaxed mt-4">
              To exercise any of these rights, please contact us at the details provided below.
            </p>
          </section>
          
          {/* Cookies */}
          <section>
            <h2 className="text-2xl font-heading font-semibold text-gray-900 mb-4">8. Cookies</h2>
            <p className="text-gray-600 font-body leading-relaxed">
              We use cookies and similar tracking technologies to track activity on our platform and store certain information. Cookies are small data files placed on your device. You can instruct your browser to refuse all cookies or indicate when a cookie is being sent.
            </p>
            <p className="text-gray-600 font-body leading-relaxed mt-4">
              We use essential cookies for authentication and session management, and may use analytics cookies to understand how users interact with our platform.
            </p>
          </section>
          
          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-heading font-semibold text-gray-900 mb-4">9. Data Retention</h2>
            <p className="text-gray-600 font-body leading-relaxed">
              We retain your personal information for as long as necessary to fulfil the purposes for which it was collected, including to satisfy legal, accounting, or reporting requirements. Contract and payment records may be retained for up to 7 years for tax and legal compliance purposes.
            </p>
          </section>
          
          {/* Changes to This Policy */}
          <section>
            <h2 className="text-2xl font-heading font-semibold text-gray-900 mb-4">10. Changes to This Privacy Policy</h2>
            <p className="text-gray-600 font-body leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. You are advised to review this Privacy Policy periodically for any changes.
            </p>
          </section>
          
          {/* Contact Us */}
          <section>
            <h2 className="text-2xl font-heading font-semibold text-gray-900 mb-4">11. Contact Us</h2>
            <p className="text-gray-600 font-body leading-relaxed">
              If you have any questions about this Privacy Policy or our data practices, please contact us at:
            </p>
            <div className="mt-4 p-4 bg-gray-100 rounded-lg">
              <p className="text-gray-700 font-body"><strong>Email:</strong> privacy@paify.co</p>
              <p className="text-gray-700 font-body mt-2"><strong>Address:</strong> United Kingdom</p>
            </div>
          </section>
        </div>
        
        {/* Footer Links */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="flex space-x-6">
            <a href="/terms-of-service" className="text-[#e6007e] hover:underline font-body">Terms of Service</a>
            <a href="/" className="text-[#e6007e] hover:underline font-body">Back to Home</a>
          </div>
        </div>
      </main>
    </div>
  );
};

// Terms of Service Page Component


export default PrivacyPolicyPage;
